import { forwardRef } from 'react';

import { Slot, Slottable } from '@radix-ui/react-slot';
import cn from 'classnames';

import type { FilteredPackage } from '@/infra/types/voyageInfo/package';

import { getBarTabText } from '@/features/promo/lib/getBarTabText';
import { selectMerchandisingBannerDetails } from '@/features/promoBanners/selectors';
import { useAppSelector } from '@/store';

import { useGetBarTabAmount } from '../lib/useGetBarTabAmount';
import { useIsPromoActive } from '../lib/useIsPromoActive';

import './PackageCard.scss';

export type TProps = {
  duration: number;
  isBarTab: boolean;
  isSale: boolean;
};

const SaleBadge = ({ duration, isBarTab, isSale }: TProps) => {
  const { voyageCardBarTabTitle, voyageCardMerchandisingTitle: voyageCardBannerTitle } =
    useAppSelector(selectMerchandisingBannerDetails) || {};
  const barTabAmount = useGetBarTabAmount(duration);
  const barTabText = getBarTabText(voyageCardBarTabTitle, barTabAmount);
  if (!isSale && !isBarTab) return null;

  return (
    <div className="PackageCard__badge">
      {!!barTabText && <div className="barTab">{barTabText}</div>}
      {isSale && voyageCardBannerTitle && <div className="sale">{voyageCardBannerTitle}</div>}
    </div>
  );
};

type TSaleBadgeHostProps = {
  children: React.ReactElement;
  className?: string;
  packageData: FilteredPackage;
};

export const WithSaleBadge = forwardRef<HTMLDivElement, TSaleBadgeHostProps>(({ children, packageData }, ref) => {
  const isSale = useIsPromoActive('voyageCardMerchandising', packageData.sailingList);
  const isBarTab = useIsPromoActive('voyageCardBarTab', packageData.sailingList);
  return (
    <>
      <Slot
        className={cn((isSale || isBarTab) && 'sale', packageData?.isPortChangeAvailable && 'portChange')}
        ref={ref}
      >
        <Slottable>{children}</Slottable>
        <SaleBadge duration={packageData.duration} isBarTab={isBarTab} isSale={isSale} />
      </Slot>
    </>
  );
});
WithSaleBadge.displayName = 'WithSaleBadge';
